import React, { memo, useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Card, CardContent, Fab, LinearProgress } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import _ from 'lodash'

import styles from './styles'
import { combineStyles } from 'utils'
import generalStyles from 'styles/common'
import { EJobActionTypes } from 'models'
import { StoreValue } from 'store'

const CreateJobProgressView: React.FunctionComponent<any> = ({
	storeDispatch,
	job,
	activeStep,
	basicInformationStep,
	detailsStep,
	teamsStep,
	postJob,
	handleSelectedStep,
}) => {
	const {
		title,
		jobDescription,
		expiryDate,
		jobType,
		contractType,
		highlightProgressBar,
		salary,
		skills,
		benefits,
		locations,
		jobTypeLoc,
		seniorityLevel,
		defaultCompanyOffices,
		team,
	} = job

	const { t } = useTranslation()
	const combinedStyles: any = combineStyles(generalStyles, styles)
	const useStyles = makeStyles(combinedStyles)
	const classes = useStyles()
	const [steps, setSteps] = useState([
		{
			title: 'Basic Information',
			value: basicInformationStep,
			isActive: activeStep === 0,
		},
		{
			title: 'Details',
			value: detailsStep,
			isActive: activeStep === 1,
		},
		{
			title: 'Teams',
			value: teamsStep,
			isActive: activeStep === 2,
		},
		{
			title: 'Post job',
			value: postJob,
			isActive: activeStep === 3,
		},
	])

	const handleGoToSelectedStep = useCallback(
		(nextStep: number) => {
			// if (activeStep === nextStep) return
			switch (activeStep) {
				case 0:
					{
						const checkValidity: boolean =
							_.isEmpty(title) ||
							_.isEmpty(jobDescription) ||
							_.isEmpty(expiryDate)
						if (checkValidity) {
							storeDispatch({
								type: EJobActionTypes.setError,
								errors: {
									title: _.isEmpty(title),
									jobDescription: _.isEmpty(jobDescription),
									expiryDate: _.isEmpty(expiryDate),
								},
							})
						} else handleSelectedStep(nextStep)
					}
					break
				case 1:
					{
						let isSalaryInvalid = true
						const { amountMin, amountMax, currency, rate } = salary

						if (
							(!amountMin && !amountMax && !currency && !rate) ||
							(amountMin && amountMax && currency && rate)
						) {
							// if all salary fields are empty or filled
							isSalaryInvalid = false
						}
						if (nextStep < activeStep && !isSalaryInvalid) {
							handleSelectedStep(nextStep)
							return
						}

						const checkValidity: boolean =
							_.isEmpty(skills) ||
							_.isEmpty(benefits) ||
							_.isEmpty(locations) ||
							_.isEmpty(contractType) ||
							_.isEmpty(jobType) ||
							_.isEmpty(jobTypeLoc) ||
							_.isEmpty(seniorityLevel) ||
							isSalaryInvalid
						if (checkValidity) {
							// check for salary validation. Salary is optional, but if the user complete one field from salary section all other fields that are related with salary will become mandatory
							let salaryValidation: {
								amountMin: boolean
								amountMax: boolean
								currency: boolean
								rate: boolean
								[key: string]: any
							} = {
								amountMin: false,
								amountMax: false,
								currency: false,
								rate: false,
							}

							let checkIfAllSalaryFieldsAreEmpty = true
							for (let item in salary) {
								salaryValidation[item] = !salary[item]
								if (!!salary[item]) {
									checkIfAllSalaryFieldsAreEmpty = false
								}
							}
							if (checkIfAllSalaryFieldsAreEmpty) {
								// if all the fields are empty I will not throw an error
								salaryValidation = {
									amountMin: false,
									amountMax: false,
									currency: false,
									rate: false,
								}
							}

							storeDispatch({
								type: EJobActionTypes.setError,
								errors: {
									skills: _.isEmpty(skills),
									benefits: _.isEmpty(benefits),
									locations: _.isEmpty(locations),
									contractType: _.isEmpty(contractType),
									jobType: _.isEmpty(jobType),
									jobTypeLoc: _.isEmpty(jobTypeLoc),
									seniorityLevel: _.isEmpty(seniorityLevel),
									salary: salaryValidation,
								},
							})
						} else handleSelectedStep(nextStep)
					}
					break
				case 2:
					if (nextStep < activeStep) {
						handleSelectedStep(nextStep)
						return
					}

					if (defaultCompanyOffices) {
						// if the company has teams.
						const checkValidity: boolean = _.isEmpty(team)
						if (checkValidity) {
							storeDispatch({
								type: EJobActionTypes.setError,
								errors: {
									team: _.isEmpty(team),
								},
							})
						} else handleSelectedStep(nextStep)
					} else {
						handleSelectedStep(nextStep)
					}
					break
				case 3:
					window.scrollTo(0, 9999)
					break
				default:
					handleSelectedStep(nextStep)
					break
			}
		},
		[
			salary,
			activeStep,
			handleSelectedStep,
			expiryDate,
			jobDescription,
			storeDispatch,
			title,
			contractType,
			jobType,
			benefits,
			jobTypeLoc,
			locations,
			seniorityLevel,
			skills,
			defaultCompanyOffices,
			team,
		],
	)

	useEffect(() => {
		setSteps([
			{
				title: 'Basic Information',
				value: basicInformationStep,
				isActive: activeStep === 0,
			},
			{
				title: 'Details',
				value: detailsStep,
				isActive: activeStep === 1,
			},
			{
				title: 'Teams',
				value: teamsStep,
				isActive: activeStep === 2,
			},
			{
				title: 'Post job',
				value: postJob,
				isActive: activeStep === 3,
			},
		])
	}, [
		basicInformationStep,
		detailsStep,
		teamsStep,
		handleSelectedStep,
		activeStep,
		postJob,
	])

	let stepsChecked = 0
	let completed1 = 0

	steps.forEach(item => {
		if (item.value) stepsChecked++
	})
	completed1 = Math.ceil((stepsChecked / 4) * 100)
	return (
		<Card
			// style={{ width: '650px' }}
			// classes={{
			// 	root: classes.cardRoot,
			// }}
			classes={{
				root: classNames(classes.cardRoot, classes.boxShadow),
			}}
			className={classNames({
				[classes.cardRootHighlighted]: highlightProgressBar,
			})}
		>
			<CardContent
				classes={{
					root: classes.cardContent,
				}}
			>
				<div className={classes.cardContentTitle}>
					{t('Creating job progress')}
				</div>
				<div style={{ marginTop: '10px' }}>
					<LinearProgress
						variant="determinate"
						color="primary"
						value={completed1}
						classes={{
							barColorPrimary: classes.colorPrimary,
							root: classes.progressRoot,
							bar1Determinate: classes.bar1Determinate,
						}}
					/>
				</div>
				<div className={classes.progressSteps}>
					{steps.map((item: any, index: any) => {
						let cursorStyle = { cursor: 'default' }
						return (
							<div
								key={`key-${index}`}
								className={classes.progressStepsItem}
								onClick={() => handleGoToSelectedStep(index)}
							>
								<Fab
									disabled={!item.value}
									className={classes.stepItem}
									classes={{
										disabled: classes.fabDisabled,
									}}
								>
									{item.value ? <CheckIcon /> : ''}
								</Fab>
								<span
									style={cursorStyle}
									className={
										item.value
											? classes.stepItemTitle
											: classes.stepTitleDisabled
									}
								>
									<span
										className={classNames({
											[classes.activeStep]: item.isActive,
										})}
									>
										{item.title}
									</span>
								</span>
							</div>
						)
					})}
				</div>
			</CardContent>
		</Card>
	)
}

const CreateJobProgress = memo((props: any) => (
	<StoreValue keys={['job', 'storeDispatch']}>
		<CreateJobProgressView {...props} />
	</StoreValue>
))

export default memo(CreateJobProgress)
